import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import {
  MultiInputDateRangeField,
  MultiInputDateRangeFieldProps,
} from '@mui/x-date-pickers-pro/MultiInputDateRangeField';
import type { DateRange } from '@mui/x-date-pickers-pro';
import {
  Box,
  StackProps,
  SxProps,
  Theme,
  TypographyProps,
} from '@mui/material';
import React, { useRef } from 'react';
import { Control, Controller, FieldErrors, FieldValues } from 'react-hook-form';
import { toPickerRange } from './pickerValue';
import { RangeCalendarHeader5 } from './RangeCalendarHeader5';

const RANGE_ROOT_STYLE: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignSelf: 'center',
  width: '100%',
};

// The v6 root is a spaced Stack; this keeps the v5 flex row the inputs sat in.
const RangeFieldRoot = React.forwardRef<HTMLDivElement, StackProps>(
  function RangeFieldRoot({ children, onBlur }, ref) {
    return (
      <div ref={ref} onBlur={onBlur} style={RANGE_ROOT_STYLE}>
        {children}
      </div>
    );
  }
);

function RangeFieldSeparator({ children }: TypographyProps) {
  return (
    <Box
      sx={{
        // Use margin, not padding: some containers (e.g. the
        // resource details drawer) apply a blanket
        // `.MuiBox-root { padding: 0 }` reset that would zero px
        // here, collapsing the separator against the next input.
        mx: 2,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      {children}
    </Box>
  );
}

const RANGE_FIELD_SLOTS = {
  root: RangeFieldRoot,
  separator: RangeFieldSeparator,
};

// v8 range pickers default to one input and no longer take root or separator
// slots, so the field is the two-input one with those slots set on it.
const RangeField = React.forwardRef<
  HTMLDivElement,
  MultiInputDateRangeFieldProps<false>
>(function RangeField(props, ref) {
  return (
    <MultiInputDateRangeField {...props} ref={ref} slots={RANGE_FIELD_SLOTS} />
  );
});

const RANGE_SLOTS = { field: RangeField, calendarHeader: RangeCalendarHeader5 };

export const DateRangeField = function ({
  id,
  name,
  label,
  control,
  startLabel,
  endLabel,
  disablePast,
  errors,
  minDate,
  maxDate,
  inputSx,
  separator = 'to',
}: {
  id: string;
  name: string;
  label: string;
  control: Control<FieldValues>;
  format?: string;
  startLabel?: string;
  endLabel?: string;
  disablePast?: boolean;
  errors?: FieldErrors | null;
  minDate?: Date;
  maxDate?: Date;
  /** Optional style overrides applied to both date TextFields. */
  inputSx?: SxProps<Theme>;
  /** Separator shown between the two inputs. Defaults to "to". */
  separator?: string;
}) {
  const now = useRef(new Date()).current;
  const [open, setOpen] = React.useState(false);
  const [isAccept, setIsAccept] = React.useState(false);
  const [isTextField, setIsTextField] = React.useState(false);
  const [fieldRoot, setFieldRoot] = React.useState<HTMLDivElement | null>(null);

  const createTitle = (text: string) => {
    const result = text.replace(/([A-Z])/g, ' $1');
    return result.charAt(0).toUpperCase() + result.slice(1);
  };

  return (
    <Controller
      rules={{ required: true }}
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        return (
          <DateRangePicker
            open={open}
            minDate={minDate ?? undefined}
            maxDate={maxDate ?? undefined}
            onAccept={() => {
              setIsAccept(true);
            }}
            onClose={() => {
              setIsAccept(false);
              if (!isTextField) {
                setOpen(false);
                setIsTextField(false);
              }
            }}
            onOpen={() => {
              if (!isAccept) {
                setOpen(true);
              }
            }}
            disablePast={disablePast}
            calendars={1}
            label={label}
            value={toPickerRange(value, now)}
            onChange={(range: DateRange<Date>) => onChange(range)}
            enableAccessibleFieldDOMStructure={false}
            slots={RANGE_SLOTS}
            slotProps={{
              field: { dateSeparator: separator, ref: setFieldRoot },
              popper: { anchorEl: fieldRoot, placement: 'bottom' },
              textField: (ownerState) => {
                // The two-input field adds `position` to the owner state;
                // the picker-level slot type does not declare it.
                const isStart =
                  'position' in ownerState && ownerState.position === 'start';
                // These sit on the input so the picker's own focus and click
                // handlers on the TextField still run, as they did in v5.
                const inputProps = {
                  onFocus: () => setIsTextField(true),
                  onBlur: () => setIsTextField(false),
                  onClick: () => setOpen(true),
                };
                return {
                  sx: [
                    { flex: 2 },
                    ...(Array.isArray(inputSx) ? inputSx : [inputSx]),
                  ],
                  variant: 'standard',
                  label: isStart
                    ? (startLabel && createTitle(startLabel)) || 'start'
                    : (endLabel && createTitle(endLabel)) || 'end',
                  error: !!errors && !!errors[id]?.message,
                  helperText: isStart
                    ? !!errors && errors[id]?.message
                    : undefined,
                  inputProps,
                };
              },
            }}
          />
        );
      }}
    />
  );
};
